import { Link } from "@tanstack/react-router";

const DISCOVER_LINKS = [
  { label: "Hair", category: "hair_salon", categoryName: "Hair" },
  { label: "Nails", category: "nail_salon", categoryName: "Nails" },
  { label: "Skin & facials", category: "skin_care", categoryName: "Skin & facials" },
  {
    label: "Brows & lashes",
    category: "eyebrows_lashes",
    categoryName: "Brows & lashes",
  },
  { label: "Barber", category: "barbershop", categoryName: "Barber" },
  { label: "Massage", category: "massage", categoryName: "Massage" },
] as const;

const DEMO_REQUEST_HREF = "mailto:hello@slotique.app?subject=Slotique%20demo%20request";
const PRICING_QUESTION_HREF = "mailto:hello@slotique.app?subject=Slotique%20pricing%20question";
const ACCESSIBILITY_SUPPORT_HREF =
  "mailto:hello@slotique.app?subject=Slotique%20accessibility%20support";

function ConsumerFooter() {
  return (
    <footer className="bg-ink text-white/70 px-5 pt-14 pb-8">
      <div className="mx-auto grid max-w-[1200px] grid-cols-1 gap-10 min-[720px]:grid-cols-[2fr_1fr_1fr]">
        {/* Brand */}
        <div>
          <h3 className="font-display text-[22px] font-semibold tracking-tight text-white mb-3">
            Slotique
          </h3>
          <p className="text-[14px] leading-normal mb-4 max-w-[360px]">
            Find a salon, explore its services, and manage your appointments.
          </p>
          <a
            href="mailto:hello@slotique.app"
            className="inline-flex min-h-11 items-center text-[14px] text-white/70 no-underline transition-colors duration-short ease-default hover:text-white focus-visible:outline-2 focus-visible:outline-electric-700 focus-visible:outline-offset-4"
          >
            Contact support
          </a>
        </div>

        {/* Discover */}
        <div>
          <FooterHeading>Discover</FooterHeading>
          <FooterList>
            {DISCOVER_LINKS.map((link) => (
              <FooterSearchLink
                key={link.category}
                category={link.category}
                categoryName={link.categoryName}
              >
                {link.label}
              </FooterSearchLink>
            ))}
          </FooterList>
        </div>

        {/* For pros */}
        <div>
          <FooterHeading>For pros</FooterHeading>
          <FooterList>
            <FooterLink to="/onboarding/start">List your salon</FooterLink>
            <FooterLinkExternal href={PRICING_QUESTION_HREF}>Ask about pricing</FooterLinkExternal>
            <FooterLinkExternal href={DEMO_REQUEST_HREF}>Request a demo</FooterLinkExternal>
            {/* Server-rendered (BlogController), so a full page load rather than a router Link. */}
            <FooterLinkExternal href="/blog/">Blog</FooterLinkExternal>
          </FooterList>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="mx-auto mt-10 flex max-w-[1200px] flex-col items-start gap-3 border-t border-white/10 pt-6 text-[12px] text-white/50 min-[720px]:flex-row min-[720px]:items-center min-[720px]:justify-between">
        <span>&copy; {new Date().getFullYear()} Slotique</span>
        <div className="flex flex-wrap gap-5">
          <BottomLink to="/terms">Terms</BottomLink>
          <BottomLink to="/privacy">Privacy</BottomLink>
          <BottomLink to="/cookies">Cookies</BottomLink>
          <BottomLinkExternal href={ACCESSIBILITY_SUPPORT_HREF}>Accessibility</BottomLinkExternal>
        </div>
      </div>
    </footer>
  );
}

function FooterHeading({ children }: { children: React.ReactNode }) {
  return (
    <h4 className="mb-4 text-[12px] font-bold uppercase tracking-[0.08em] text-white">
      {children}
    </h4>
  );
}

function FooterList({ children }: { children: React.ReactNode }) {
  return <ul className="flex list-none flex-col gap-2.5 p-0 m-0">{children}</ul>;
}

function FooterLink({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <li>
      <Link
        to={to}
        className="text-white/70 no-underline text-[14px] transition-colors duration-short ease-default hover:!text-white"
      >
        {children}
      </Link>
    </li>
  );
}

function FooterSearchLink({
  category,
  categoryName,
  children,
}: {
  category: string;
  categoryName: string;
  children: React.ReactNode;
}) {
  return (
    <li>
      <Link
        to="/search"
        search={{ category, category_name: categoryName }}
        className="text-white/70 no-underline text-[14px] transition-colors duration-short ease-default hover:!text-white"
      >
        {children}
      </Link>
    </li>
  );
}

function FooterLinkExternal({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <li>
      <a
        href={href}
        className="text-white/70 no-underline text-[14px] transition-colors duration-short ease-default hover:!text-white"
      >
        {children}
      </a>
    </li>
  );
}

function BottomLink({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <Link to={to} className="text-inherit no-underline hover:!text-white">
      {children}
    </Link>
  );
}

function BottomLinkExternal({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <a href={href} className="text-inherit no-underline hover:!text-white">
      {children}
    </a>
  );
}

export { ConsumerFooter };
